import { useState, useEffect } from "react";
import { api } from "@/lib/api";

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  enrollmentId: string;
  courseName: string;
  editionLabel?: string;
  coursePrice: number;
  amountPaid: number;
  paymentReference?: string;
  onSuccess: () => void;
}

export default function PaymentModal({
  isOpen,
  onClose,
  enrollmentId,
  courseName,
  editionLabel,
  coursePrice,
  amountPaid,
  paymentReference = "",
  onSuccess,
}: PaymentModalProps) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("transferencia");
  const [reference, setReference] = useState("");
  const [proof, setProof] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const remaining = Math.max(0, Math.round((coursePrice - amountPaid) * 100) / 100);

  useEffect(() => {
    if (isOpen) {
      setReference(paymentReference);
      setAmount(remaining > 0 ? String(remaining) : "");
      setError("");
      setProof(null);
    }
  }, [isOpen, paymentReference, remaining]);

  const copyReference = () => {
    if (paymentReference) navigator.clipboard.writeText(paymentReference);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const num = parseFloat(amount);
    if (!amount || num <= 0) {
      setError("Ingrese un monto válido");
      return;
    }
    if (num > remaining + 0.009) {
      setError(`El monto no puede superar el saldo pendiente ($${remaining.toLocaleString("en-US")} USD)`);
      return;
    }
    if (method === "transferencia" && !reference.trim()) {
      setError("Indique la referencia bancaria del pago");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("enrollment_id", enrollmentId);
      fd.append("amount", amount);
      fd.append("payment_method", method);
      fd.append("reference", reference.trim() || paymentReference);
      if (proof) fd.append("proof", proof);
      await api.submitPayment(fd);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al enviar pago");
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-2xl w-full max-w-md p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-bold text-[var(--color-text-primary)] mb-1">Registrar pago</h2>
        <p className="text-xs text-[var(--color-text-muted)] mb-1">{courseName}</p>
        {editionLabel && <p className="text-xs text-[var(--color-text-accent)] mb-4">{editionLabel}</p>}

        <div className="bg-[var(--color-bg-tertiary)] rounded-xl p-4 mb-4 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-[var(--color-text-muted)]">Precio del curso</span>
            <span>${coursePrice.toLocaleString("en-US")} USD</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[var(--color-text-muted)]">Pagado</span>
            <span className="text-green-400">${amountPaid.toLocaleString("en-US")} USD</span>
          </div>
          <div className="flex justify-between font-semibold border-t border-[var(--color-border)] pt-2">
            <span>Pendiente</span>
            <span className="text-[var(--color-accent-oil)]">${remaining.toLocaleString("en-US")} USD</span>
          </div>
        </div>

        {paymentReference && (
          <div className="mb-4 p-3 border border-[var(--color-border)] rounded-xl">
            <p className="text-xs text-[var(--color-text-muted)] mb-1">Referencia única de inscripción (use en transferencia)</p>
            <div className="flex items-center gap-2">
              <code className="text-sm font-mono text-[var(--color-accent-oil)] flex-1 break-all">{paymentReference}</code>
              <button type="button" onClick={copyReference} className="btn-secondary text-xs py-1 px-2 cursor-pointer">Copiar</button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-[var(--color-text-secondary)] mb-1">Monto a pagar</label>
            <input type="number" className="input-field" value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder={`Máx. ${remaining}`} min="1" max={remaining} step="0.01" required />
          </div>
          <div>
            <label className="block text-sm text-[var(--color-text-secondary)] mb-1">Método de pago</label>
            <select className="input-field" value={method} onChange={(e) => setMethod(e.target.value)}>
              <option value="transferencia">Transferencia bancaria</option>
              <option value="deposito">Depósito</option>
              <option value="efectivo">Efectivo</option>
              <option value="tarjeta">Tarjeta</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-[var(--color-text-secondary)] mb-1">
              Referencia bancaria {method === "transferencia" ? "*" : "(opcional)"}
            </label>
            <input className="input-field" value={reference} onChange={(e) => setReference(e.target.value)}
              placeholder="Folio, CLABE, número de operación..." required={method === "transferencia"} />
          </div>
          <div>
            <label className="block text-sm text-[var(--color-text-secondary)] mb-1">Comprobante</label>
            <input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={(e) => setProof(e.target.files?.[0] || null)}
              className="input-field file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-[var(--color-accent-oil)] file:text-[#080f1a] file:text-sm" />
          </div>
          {error && <p className="text-sm text-red-400">{error}</p>}
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="btn-secondary flex-1 cursor-pointer">Cancelar</button>
            <button type="submit" disabled={loading} className="btn-primary flex-1 cursor-pointer disabled:opacity-50">
              {loading ? "Enviando..." : "Enviar pago"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
